import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, FileDown, Grip, Tally2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import StaffTableSkeleton from "@/components/skeleton/Staff-skeletion";
import Paginate from "@/components/lib/paginat";
import { Link } from "react-router-dom";
import OnlyTableSkeleton from "@/components/skeleton/only-table-skeleton";
import useAuth from "@/hooks/useAuth";
import { DiscountManagementTable } from "@/components/backoffice/settings/discount-management-table";
import { TabsContent } from "@radix-ui/react-tabs";
import {
  deleteDiscountTypeApi,
  getAllCustomerTypeApi,
  getAllDiscountApi,
} from "@/api/settings/discount";
import TableSkeleton from "@/components/skeleton/table-skeletion";
import TableDataSkeleton from "@/components/skeleton/table-data-skeletion";
import { toast } from "react-hot-toast";
import hasPermission from "@/hooks/hasPermission";

function Discount() {
  const { user } = useAuth();
  const [allDiscountData, setAllDiscountData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [customerTypes, setCustomerTypes] = useState([]);
  const [customerType, setCustomerType] = useState("0");
  const [status, setStatus] = useState("0");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    getAllDiscountApi(
      user?.restaurant_id,
      perPage,
      currentPage,
      search,
      customerType,
      status,
    ).then((data) => {
      setLoading(false);
      if (data.status == "success") {
        setAllDiscountData(data?.data?.discounts);
        setPagination(data?.data?.pagination);
      }
    });
  }, [perPage, search, customerType, status, currentPage]);

  useEffect(() => {
    setLoading(true);
    getAllCustomerTypeApi(user?.restaurant_id, 0).then((data) => {
      setLoading(false);
      if (data.status == "success") {
        setCustomerTypes(data?.data?.customer_types);
      }
    });
  }, []);

  function deleteDiscount(id) {
    deleteDiscountTypeApi(id).then((data) => {
      if (data.status == "success") {
        toast.success("Discount Deleted Successfully");
        const filteredData = allDiscountData.filter((item) => item?.id !== id);
        setAllDiscountData(filteredData);
      }
    });
  }

  return (
    <TabsContent value="discount">
      <div className="container flex flex-col gap-4 mt-5">
        <div className="flex justify-between items-start md:items-center md:flex-row flex-col gap-5  w-full">
          <div className="flex gap-5 text-white"></div>
          {hasPermission("back_office.settings.discount_create") && (
            <Link to="/backoffice/settings/discount/add">
              <Button className="bg-cyellow hover:bg-cyellow text-black">
                <Plus className="mr-2 h-4 w-4" />
                Add New Discount
              </Button>
            </Link>
          )}
        </div>

        <Card className="bg-cblack3 border-0">
          <CardContent className="flex justify-between flex-col gap-5 items-center mt-4">
            <div className="w-full flex justify-between lg:flex-row flex-col gap-5 items-start lg:items-center">
              <div className="flex justify-between items-center gap-3">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label className="text-base text-white" htmlFor="">
                    Search
                  </Label>
                  <Input
                    className="text-gray-300"
                    type="text"
                    id=""
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by Name"
                  />
                </div>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label className="text-base text-white" htmlFor="">
                    Customer Type
                  </Label>
                  <Select
                    onValueChange={(value) => setCustomerType(value)}
                    value={customerType.toString()}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="0">All</SelectItem>
                        {customerTypes.map((type) => (
                          <SelectItem
                            key={type?.id}
                            value={type?.id?.toString()}
                          >
                            {type?.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label className="text-base text-white" htmlFor="">
                    Status
                  </Label>
                  <div className="relative">
                    <Select
                      onValueChange={(value) => setStatus(value)}
                      value={status.toString()}
                    >
                      <SelectTrigger className="w-[110px]">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="0">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="flex flex-col  gap-1.5 justify-end items-end">
                <div className="flex gap-3 justify-center items-center">
                  <div className="flex gap-1 justify-center items-center">
                    <h3 className="text-white">Show</h3>
                    <Select
                      onValueChange={(value) => setPerPage(Number(value))}
                      value={perPage.toString()}
                    >
                      <SelectTrigger className="w-[60px]">
                        <SelectValue placeholder="10" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="40">40</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full">
              {loading ? (
                <TableDataSkeleton />
              ) : (
                <DiscountManagementTable
                  setTableData={setAllDiscountData}
                  tableData={allDiscountData}
                  deleteDiscount={deleteDiscount}
                />
              )}

              {!loading ? (
                <Paginate
                  className="mt-2"
                  pagination={pagination}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
}

export default Discount;
